import React, { useMemo } from 'react';
import type { MealBreakConfig, RateMultiplier } from '@/types';
import { CustomizedFieldIndicator } from '../../../form/CustomizedFieldIndicator';
import { NumberInputRow } from '@/components/form/NumberInputRow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fieldLabelClass, fieldDescriptionClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { buildCreditBasisOptions, getCreditBasisLabel, getCreditBasisDescription } from '@/components/config_forms/shared/utils/creditBasisUtils';

interface Props {
  safeConfig: MealBreakConfig;
  creditIntervalMinutesProps?: Record<string, unknown>;
  creditIntervalMinutesError?: React.ReactNode;
  timeCreditMinutesProps?: Record<string, unknown>;
  timeCreditMinutesError?: React.ReactNode;
  mealConfigCustomized: Partial<Record<keyof MealBreakConfig, boolean>>;
  context: 'user' | 'client';
  mealTriggerMode?: 'deadline' | 'count';
  onMealConfigChange: (updates: Partial<MealBreakConfig>) => void;
  rateMultipliers: RateMultiplier[];
}

export const CreditExtraTimeSection: React.FC<Props> = ({
  safeConfig,
  creditIntervalMinutesProps,
  creditIntervalMinutesError,
  timeCreditMinutesProps,
  timeCreditMinutesError,
  mealConfigCustomized,
  context,
  mealTriggerMode,
  onMealConfigChange,
  rateMultipliers,
}) => {
  const isCountMode = mealTriggerMode === 'count';

  // Build a sorted ladder from rateMultipliers for the shared utils
  const sortedLadder = useMemo(() =>
    [...rateMultipliers]
      .filter(m => m.defaultMultiplier != null)
      .sort((a, b) => (a.defaultMultiplier ?? 0) - (b.defaultMultiplier ?? 0))
      .map(m => ({ code: m.code, name: m.name, multiplier: m.defaultMultiplier! })),
    [rateMultipliers],
  );

  const creditBasisOptions = useMemo(() => buildCreditBasisOptions(sortedLadder), [sortedLadder]);
  const selectedValue = safeConfig.premiumBasis ?? 'STRAIGHT_TIME';
  const basisLabel = getCreditBasisLabel(selectedValue, sortedLadder);
  const descriptionText = getCreditBasisDescription(selectedValue, basisLabel);

  return (
    <div className="space-y-4">
      {/* Credit Interval is only relevant in window mode (cadence-based) */}
      {!isCountMode && (
        <NumberInputRow
          id="surcharge-cadence-credit"
          label={
            <div className="flex items-center gap-1.5">
              <span>Credit Interval</span>
              <CustomizedFieldIndicator isCustomized={mealConfigCustomized.creditIntervalMinutes} context={context} />
            </div>
          }
          description="How often extra time is credited after the Meal Break Deadline is missed. One credit is applied per interval."
          unit="min"
          inputProps={creditIntervalMinutesProps ?? {}}
          errorElement={creditIntervalMinutesError}
          inputWidth="half"
        />
      )}

      <NumberInputRow
        id="credit-extra-time-minutes"
        label={
          <div className="flex items-center gap-1.5">
            <span>Time Credit</span>
            <CustomizedFieldIndicator isCustomized={mealConfigCustomized.timeCreditMinutes} context={context} />
          </div>
        }
        description={isCountMode
          ? "Extra minutes of pay credited for each missed meal break."
          : "Extra minutes of pay credited at your current rate for each surcharge interval."
        }
        unit="min"
        inputProps={timeCreditMinutesProps ?? {}}
        errorElement={timeCreditMinutesError}
        inputWidth="half"
      />

      {isCountMode && (
        <div className="flex flex-col gap-1 md:flex-row md:items-center w-full md:justify-start">
          <div className="flex flex-col gap-1 md:w-1/2">
            <div className="flex items-center gap-1.5">
              <span className={fieldLabelClass}>Credit Basis</span>
              <CustomizedFieldIndicator isCustomized={mealConfigCustomized.premiumBasis} context={context} />
            </div>
            <p className={fieldDescriptionClass}>
              {descriptionText}
            </p>
          </div>
          <div className="flex flex-col items-start w-full md:w-1/2 md:items-end">
            <Select
              value={selectedValue}
              onValueChange={(v) => onMealConfigChange({ premiumBasis: v })}
            >
              <SelectTrigger className="w-full md:w-auto md:min-w-[220px] min-h-[44px] input-recessed focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {creditBasisOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
};
